import Phaser from 'phaser';

/**
 * Virtual joystick + action buttons for touch/mobile devices.
 * Rendered as part of the UI scene overlay.
 */
export class TouchControls {
  private scene: Phaser.Scene;

  // Joystick state
  private joystickBase!: Phaser.GameObjects.Arc;
  private joystickThumb!: Phaser.GameObjects.Arc;
  private joystickContainer!: Phaser.GameObjects.Container;
  private joystickPointer: Phaser.Input.Pointer | null = null;
  private joystickRadius = 40;
  private thumbRadius = 18;

  // Output: normalized direction vector (-1 to 1)
  dirX: number = 0;
  dirY: number = 0;

  // Action buttons
  private interactBtn!: Phaser.GameObjects.Container;
  private inventoryBtn!: Phaser.GameObjects.Container;
  private attackBtn!: Phaser.GameObjects.Container;
  private sentinelBtn!: Phaser.GameObjects.Container;
  private interactCallback: () => void;
  private inventoryCallback: () => void;
  private attackCallback: () => void;
  private sentinelCallback: () => void;

  // Visibility
  private isTouchDevice: boolean;

  constructor(
    scene: Phaser.Scene,
    onInteract: () => void,
    onInventory: () => void,
    onAttack?: () => void,
    onSentinel?: () => void,
  ) {
    this.scene = scene;
    this.interactCallback = onInteract;
    this.inventoryCallback = onInventory;
    this.attackCallback = onAttack ?? (() => {});
    this.sentinelCallback = onSentinel ?? (() => {});
    this.isTouchDevice = this.detectTouch();

    if (this.isTouchDevice) {
      this.createJoystick();
      this.createActionButtons();
    }
  }

  private detectTouch(): boolean {
    return this.scene.sys.game.device.input.touch;
  }

  // ==========================================================================
  // Virtual Joystick
  // ==========================================================================

  private createJoystick(): void {
    const height = this.scene.cameras.main.height;
    const cx = 80;
    const cy = height - 100;

    this.joystickContainer = this.scene.add.container(cx, cy);
    this.joystickContainer.setDepth(300);

    // Base circle (semi-transparent)
    this.joystickBase = this.scene.add.circle(0, 0, this.joystickRadius, 0x000000, 0.3);
    this.joystickBase.setStrokeStyle(2, 0x6aaa3a, 0.5);
    this.joystickContainer.add(this.joystickBase);

    // D-pad hints
    const hintAlpha = 0.2;
    const hintDist = this.joystickRadius * 0.55;
    const arrows = [
      { x: 0, y: -hintDist, label: '\u25B2' },   // up
      { x: 0, y: hintDist, label: '\u25BC' },     // down
      { x: -hintDist, y: 0, label: '\u25C0' },    // left
      { x: hintDist, y: 0, label: '\u25B6' },     // right
    ];
    arrows.forEach(a => {
      const t = this.scene.add.text(a.x, a.y, a.label, {
        fontFamily: 'monospace',
        fontSize: '10px',
        color: '#ffffff',
      });
      t.setOrigin(0.5, 0.5);
      t.setAlpha(hintAlpha);
      this.joystickContainer.add(t);
    });

    // Thumb (draggable knob)
    this.joystickThumb = this.scene.add.circle(0, 0, this.thumbRadius, 0x6aaa3a, 0.6);
    this.joystickThumb.setStrokeStyle(2, 0x8bcc5a, 0.8);
    this.joystickContainer.add(this.joystickThumb);

    // Make the whole joystick area interactive
    // Use a large invisible hit zone
    const hitZone = this.scene.add.circle(0, 0, this.joystickRadius + 20, 0x000000, 0.001);
    hitZone.setInteractive();
    this.joystickContainer.add(hitZone);

    // Touch/pointer events
    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Only capture if touch is in the left third of the screen
      if (pointer.x < this.scene.cameras.main.width * 0.4 && !this.joystickPointer) {
        this.joystickPointer = pointer;
        this.updateJoystickThumb(pointer);
      }
    });

    this.scene.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer === this.joystickPointer) {
        this.updateJoystickThumb(pointer);
      }
    });

    this.scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (pointer === this.joystickPointer) {
        this.joystickPointer = null;
        this.joystickThumb.setPosition(0, 0);
        this.dirX = 0;
        this.dirY = 0;
      }
    });
  }

  private updateJoystickThumb(pointer: Phaser.Input.Pointer): void {
    const cx = this.joystickContainer.x;
    const cy = this.joystickContainer.y;

    let dx = pointer.x - cx;
    let dy = pointer.y - cy;

    // Clamp to joystick radius
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > this.joystickRadius) {
      dx = (dx / dist) * this.joystickRadius;
      dy = (dy / dist) * this.joystickRadius;
    }

    this.joystickThumb.setPosition(dx, dy);

    // Normalize to -1..1, with a small dead zone
    const deadZone = 0.15;
    this.dirX = dx / this.joystickRadius;
    this.dirY = dy / this.joystickRadius;

    if (Math.abs(this.dirX) < deadZone) this.dirX = 0;
    if (Math.abs(this.dirY) < deadZone) this.dirY = 0;
  }

  // ==========================================================================
  // Action Buttons
  // ==========================================================================

  private createActionButtons(): void {
    const width = this.scene.cameras.main.width;
    const height = this.scene.cameras.main.height;

    // Interact button (bottom right)
    this.interactBtn = this.createButton(
      width - 70, height - 120,
      'E',
      'Use',
      0x4a7c4b,
      () => this.interactCallback()
    );

    // Attack button (right of interact)
    this.attackBtn = this.createButton(
      width - 130, height - 120,
      '⚔',
      'Atk',
      0xd4573b,
      () => this.attackCallback()
    );

    // Inventory button (above interact)
    this.inventoryBtn = this.createButton(
      width - 70, height - 180,
      'I',
      'Bag',
      0x5577cc,
      () => this.inventoryCallback()
    );

    // Sentinel button (above attack)
    this.sentinelBtn = this.createButton(
      width - 130, height - 180,
      'F',
      'Def',
      0x44aa66,
      () => this.sentinelCallback()
    );
  }

  private createButton(
    x: number, y: number,
    letter: string, label: string,
    color: number,
    callback: () => void,
  ): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    container.setDepth(300);

    const btnSize = 44;

    // Button background
    const bg = this.scene.add.circle(0, 0, btnSize / 2, color, 0.6);
    bg.setStrokeStyle(2, 0xffffff, 0.4);
    container.add(bg);

    // Letter
    const letterText = this.scene.add.text(0, -4, letter, {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    letterText.setOrigin(0.5, 0.5);
    container.add(letterText);

    // Label below
    const labelText = this.scene.add.text(0, 12, label, {
      fontFamily: 'monospace',
      fontSize: '8px',
      color: '#cccccc',
    });
    labelText.setOrigin(0.5, 0.5);
    container.add(labelText);

    // Hit zone
    const hitZone = this.scene.add.circle(0, 0, btnSize / 2 + 8, 0x000000, 0.001);
    hitZone.setInteractive();
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      bg.setAlpha(0.9);
      callback();
    });
    hitZone.on('pointerup', () => {
      bg.setAlpha(0.6);
    });
    hitZone.on('pointerout', () => {
      bg.setAlpha(0.6);
    });

    return container;
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  isActive(): boolean {
    return this.isTouchDevice;
  }

  getDirection(): { x: number; y: number } {
    return { x: this.dirX, y: this.dirY };
  }

  destroy(): void {
    this.joystickContainer?.destroy();
    this.interactBtn?.destroy();
    this.inventoryBtn?.destroy();
    this.attackBtn?.destroy();
    this.sentinelBtn?.destroy();
  }
}
